const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const parseUnitValue = (raw, basePx = 12) => {
  const text = String(raw || '').trim().toLowerCase();
  if (!text) return null;
  if (text === 'normal') return null;
  const m = text.match(/^([0-9]+(?:\.[0-9]+)?)\s*(px|pt)?$/i);
  if (!m) return null;
  const n = toNumber(m[1]);
  if (n === null || n <= 0) return null;
  const unit = String(m[2] || 'px').toLowerCase();
  if (unit === 'pt') return n * (96 / 72);
  return n;
};

const getStyleValue = (styleText, prop) => {
  const source = String(styleText || '');
  const escaped = String(prop || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = source.match(new RegExp(`${escaped}\\s*:\\s*([^;]+)`, 'i'));
  return m ? String(m[1] || '').trim() : '';
};

const extractTypography = (html) => {
  const source = String(html || '');
  const styleMatches = Array.from(source.matchAll(/style\s*=\s*["']([^"']+)["']/gi));
  let fontSizePx = null;
  let lineHeightPx = null;

  for (const match of styleMatches) {
    const styleText = String(match[1] || '');
    if (fontSizePx === null) {
      const fs = parseUnitValue(getStyleValue(styleText, 'font-size'));
      if (fs !== null) fontSizePx = fs;
    }
    if (lineHeightPx === null) {
      const rawLh = getStyleValue(styleText, 'line-height');
      if (rawLh) {
        const parsedLh = parseUnitValue(rawLh, fontSizePx || 12);
        if (parsedLh !== null) {
          lineHeightPx = parsedLh;
        } else {
          const ratio = toNumber(rawLh);
          if (ratio !== null && ratio > 0) {
            const base = fontSizePx || 12;
            lineHeightPx = base * ratio;
          }
        }
      }
    }
    if (fontSizePx !== null && lineHeightPx !== null) break;
  }

  const resolvedFontSize = fontSizePx || 12;
  const resolvedLineHeight = lineHeightPx || (resolvedFontSize * 1.15);
  return {
    fontSizePx: resolvedFontSize,
    lineHeightPx: Math.max(resolvedFontSize, resolvedLineHeight),
  };
};

const countLogicalLines = (html) => {
  const source = String(html || '').trim();
  if (!source) return 0;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return source.split(/\r?\n/).filter((line) => String(line || '').trim().length > 0).length || 1;
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(`<div id="hf-root">${source}</div>`, 'text/html');
  const root = doc.getElementById('hf-root');
  if (!root) return 0;

  const blockTags = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR', 'TD', 'TH']);
  let lines = 0;

  const addTextLines = (text) => {
    const raw = String(text || '').replace(/\u00A0/g, ' ');
    const nonEmpty = raw.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (nonEmpty.length > 0) {
      lines += nonEmpty.length;
    }
  };

  const walk = (node) => {
    if (!node) return;
    if (node.nodeType === 3) {
      addTextLines(node.nodeValue || '');
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node;
    if (el.tagName === 'BR') {
      lines += 1;
      return;
    }
    if (blockTags.has(el.tagName)) {
      const text = String(el.textContent || '').trim();
      const brCount = el.querySelectorAll('br').length;
      if (text.length > 0 || brCount > 0) {
        lines += Math.max(1, brCount + 1);
      }
      return;
    }
    Array.from(el.childNodes).forEach(walk);
  };

  Array.from(root.childNodes).forEach(walk);
  return Math.max(0, lines);
};

export const estimateHeaderFooterBlockHeightPx = (html) => {
  const source = String(html || '').trim();
  if (!source) return 0;
  const { lineHeightPx } = extractTypography(source);
  const lines = countLogicalLines(source);
  if (lines <= 0) return 0;
  return Math.max(0, lines * lineHeightPx);
};

export const getHeaderFooterLayoutMetrics = (html) => {
  const source = String(html || '').trim();
  if (!source) {
    return {
      lineCount: 0,
      fontSizePx: 12,
      lineHeightPx: 12 * 1.15,
      blockHeightPx: 0,
    };
  }
  const { fontSizePx, lineHeightPx } = extractTypography(source);
  const lineCount = countLogicalLines(source);
  const blockHeightPx = lineCount > 0 ? Math.max(0, lineCount * lineHeightPx) : 0;
  return {
    lineCount,
    fontSizePx,
    lineHeightPx,
    blockHeightPx,
  };
};
